
import { CHAT_DOCUMENT_EXTENSIONS, IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../constants/fileTypes'
import { VALIDATION_CONSTANTS } from '../constants/validation'
import { hasActivePhotoSwipe } from '../composables/usePhotoSwipe'
import { isValidUrl, sanitizeFileName } from './sanitization'
import { markdownToPlainText } from '@/modules/chat-matrix-fast/utils/messageRichText'

// Same file-type icons as the adverse-event registration modal
import universal from '@images/icons/files/universal.svg'
import xsl from '@images/icons/files/xls.svg'
import pdf from '@images/icons/files/pdf.svg'
import doc from '@images/icons/files/doc.svg'
import jpg from '@images/icons/files/jpg.svg'
import { debugConsoleError } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export const getFileExtension = (fileUrl: string): string => {
  if (!fileUrl || typeof fileUrl !== 'string') return ''

  const fileName = fileUrl.split('?')[0].split('#')[0].split('/').pop() || ''
  const parts = fileName.split('.')

  return parts.length > 1 ? parts.pop()?.toLowerCase() || '' : ''
}

function hasImageExtension(extension: string): boolean {
  return IMAGE_EXTENSIONS.includes(`.${extension}` as (typeof IMAGE_EXTENSIONS)[number])
}

function getImageExtensionFromQuery(url: string): string {
  const query = url.split('?')[1]?.split('#')[0] ?? ''
  if (!query) {
    return ''
  }

  for (const part of query.split('&')) {
    const [rawKey, rawValue = ''] = part.split('=')
    const key = decodeURIComponent(rawKey).toLowerCase()
    const value = decodeURIComponent(rawValue).toLowerCase()

    if (!['format', 'fm', 'ext', 'f', 'type', 'content-type', 'mime'].includes(key)) {
      continue
    }

    const fromMime = value.startsWith('image/') ? value.slice(6) : value
    if (hasImageExtension(fromMime)) {
      return fromMime
    }
  }

  return ''
}

export const isImageFile = (fileUrl: string, mimeType?: string): boolean => {
  if (mimeType?.startsWith('image/')) {
    return true
  }

  if (!fileUrl || typeof fileUrl !== 'string') return false

  const extension = getFileExtension(fileUrl)
  if (extension && hasImageExtension(extension)) {
    return true
  }

  return Boolean(getImageExtensionFromQuery(fileUrl))
}

export const isVideoFile = (fileUrl: string, mimeType?: string): boolean => {
  if (mimeType?.startsWith('video/')) {
    return true
  }

  if (!fileUrl || typeof fileUrl !== 'string') return false

  const url = fileUrl.toLowerCase().trim()
  const fileName = url.split('?')[0]

  return VIDEO_EXTENSIONS.some(ext => fileName.endsWith(ext))
}

export const isMediaFile = (fileUrl: string, mimeType?: string): boolean => {
  return isImageFile(fileUrl, mimeType) || isVideoFile(fileUrl, mimeType)
}

const URL_IN_TEXT_REGEX = /https?:\/\/[^\s<>"')\]]+/g

function normalizeMatchedUrl(match: string): string {
  return match.replace(/[.,;:!?)]+$/, '')
}

/** Извлекает ссылки на изображения из текста сообщения */
export const extractImageUrlsFromText = (text: string | undefined): string[] => {
  if (!text) {
    return []
  }

  const seen = new Set<string>()
  const urls: string[] = []

  for (const match of text.matchAll(URL_IN_TEXT_REGEX)) {
    const url = normalizeMatchedUrl(match[0])
    if (!seen.has(url) && isValidUrl(url) && isImageFile(url)) {
      seen.add(url)
      urls.push(url)
    }
  }

  return urls
}

/** Убирает ссылки на изображения из текста */
export const stripImageUrlsFromText = (text: string | undefined): string => {
  if (!text) {
    return ''
  }

  return text
    .replace(URL_IN_TEXT_REGEX, match => {
      const url = normalizeMatchedUrl(match)
      return isValidUrl(url) && isImageFile(url) ? '' : match
    })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Возвращает URL, если текст сообщения — одна ссылка на изображение */
export const getImageUrlFromText = (text: string | undefined): string | null => {
  const urls = extractImageUrlsFromText(text)
  if (urls.length === 1 && stripImageUrlsFromText(text) === '') {
    return urls[0]
  }

  return null
}

/** Hide message text when it duplicates attachment filename (Matrix body fallback). */
export const shouldShowMessageText = (
  text: string | undefined,
  attachments?: Array<{ file: string; mime_type?: string; name?: string }>,
): boolean => {
  const trimmed = text?.trim()
  if (!trimmed) {
    return false
  }

  const displayText = stripImageUrlsFromText(trimmed)
  if (!displayText) {
    return false
  }

  if (!attachments?.length) {
    return true
  }

  return !attachments.some(attachment => {
    if (
      attachment.mime_type?.startsWith('image/')
      || attachment.mime_type?.startsWith('video/')
    ) {
      return false
    }

    const fileName = attachment.name || getFileName(attachment.file)
    return displayText === fileName
  })
}

export const getFileName = (fileUrl: string): string => {
  if (!fileUrl || typeof fileUrl !== 'string') return 'Файл'

  const parts = fileUrl.split('?')[0].split('/')
  const filename = parts[parts.length - 1] || 'Файл'

  try {
    return decodeURIComponent(filename)
  } catch {
    return filename
  }
}

export const getFileIcon = (fileUrl: string): string => {
  const extension = getFileExtension(fileUrl)

  switch (extension) {
    case 'xls':
    case 'xlsx':
      return xsl
    case 'pdf':
      return pdf
    case 'doc':
    case 'docx':
      return doc
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return jpg
    default:
      return universal
  }
}

/** Длительность видео в формате m:ss */
export const formatVideoDuration = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const total = Math.floor(seconds)
  const minutes = Math.floor(total / 60)
  const secs = total % 60

  return `${minutes}:${String(secs).padStart(2, '0')}`
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Б'

  const k = 1024
  const sizes = ['Б', 'КБ', 'МБ', 'ГБ']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

export const isValidFileSize = (file: File, maxSizeBytes: number): boolean => {
  return file.size <= maxSizeBytes
}

export const isValidFileType = (
  file: File,
  allowedTypes: string[],
): boolean => {
  return allowedTypes.includes(file.type)
}

function getExtensionFromFileName(fileName: string): string {
  const parts = fileName.split('.')
  if (parts.length < 2) {
    return ''
  }

  return `.${parts.pop()!.toLowerCase()}`
}

/** Проверяет, можно ли отправить файл в чат (MIME или расширение). */
export const isAllowedChatUploadFile = (file: File): boolean => {
  const allowedMimeTypes = [
    ...VALIDATION_CONSTANTS.FILE.ALLOWED_IMAGE_TYPES,
    ...VALIDATION_CONSTANTS.FILE.ALLOWED_VIDEO_TYPES,
    ...VALIDATION_CONSTANTS.FILE.ALLOWED_DOCUMENT_TYPES,
  ]

  if (file.type && allowedMimeTypes.includes(file.type)) {
    return true
  }

  const extension = getExtensionFromFileName(file.name)
  const allowedExtensions = [
    ...IMAGE_EXTENSIONS,
    ...VIDEO_EXTENSIONS,
    ...CHAT_DOCUMENT_EXTENSIONS,
  ] as readonly string[]

  return allowedExtensions.includes(extension)
}

export const getAttachmentLabel = (
  attachment: { file: string; mime_type?: string; name?: string },
  messageText?: string,
): string => {
  if (attachment.name?.trim()) {
    return attachment.name.trim()
  }

  const trimmedMessage = messageText?.trim()
  if (trimmedMessage && shouldShowMessageText(trimmedMessage, [attachment])) {
    return trimmedMessage
  }

  if (attachment.mime_type?.startsWith('image/')) {
    return 'Изображение'
  }

  if (attachment.mime_type?.startsWith('video/')) {
    return 'Видео'
  }

  const fileName = getFileName(attachment.file)
  if (/\.[a-z0-9]{2,5}$/i.test(fileName)) {
    return fileName
  }

  return 'Файл'
}

/** Превью в списке чатов / тостов — тот же текст, что alt у вложения. */
export function formatListMessagePreview(
  messageText?: string | null,
  attachments?: Array<{ file: string; mime_type?: string; name?: string }> | null,
): string {
  const text = markdownToPlainText(messageText || '').replace(/\s+/g, ' ').trim()
  const hasAttachments = Boolean(attachments?.length)

  if (text && shouldShowMessageText(text, attachments || undefined)) {
    return text
  }

  if (hasAttachments) {
    const lastAttachment = attachments![attachments!.length - 1]
    if (lastAttachment?.file) {
      return getAttachmentLabel(lastAttachment, text)
    }
  }

  return text
}

export const openImagePreview = async (imageUrl: string, mimeType?: string): Promise<void> => {
  if (!imageUrl || typeof imageUrl !== 'string') {
    return
  }

  if (!isValidUrl(imageUrl) && !imageUrl.startsWith('blob:')) {
    debugConsoleError('Invalid image URL:', imageUrl)
    return
  }

  if (!isImageFile(imageUrl, mimeType) && !imageUrl.startsWith('blob:')) {
    return
  }

  if (typeof window !== 'undefined' && !hasActivePhotoSwipe()) {
    window.open(imageUrl, '_blank', 'noopener,noreferrer')
  }
}

export const downloadFile = async (fileUrl: string, fileName?: string): Promise<void> => {
  if (!fileUrl || typeof fileUrl !== 'string') {
    return
  }

  let downloadUrl = fileUrl

  if (fileUrl.startsWith('blob:')) {
    downloadUrl = fileUrl
  } else {
    if (!isValidUrl(fileUrl)) {
      debugConsoleError('Invalid file URL:', fileUrl)
      throw new Error('Invalid file URL')
    }

    const { isMatrixMediaHttpUrl, resolveMediaUrlForDisplay } = await import(
      '@/modules/chat-matrix-fast/services/matrixMediaUrl'
    )

    if (isMatrixMediaHttpUrl(fileUrl) || fileUrl.startsWith('mxc://')) {
      downloadUrl = await resolveMediaUrlForDisplay(fileUrl)
    }
  }

  const link = document.createElement('a')

  link.href = downloadUrl
  link.download = sanitizeFileName(fileName || getFileName(fileUrl))
  link.target = '_blank'
  link.rel = 'noopener noreferrer'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
